import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Video, Home, TrendingUp, CheckCircle, Award, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import SEO from './SEO';
import Footer from './Footer';

const BecomeSpecialist = () => {
  // Course and EducationalOccupationalCredential Schema
  const becomeSpecialistSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Course",
        "@id": "https://enrolment.bookreviewspecialists.app/become-a-video-book-review-specialist#course",
        "name": "Video Book Review Specialist Training",
        "description": "CPD Accredited training program to become a certified Video Book Review Specialist. Learn to create professional book review videos using AI tools without reading books, being on camera, or writing scripts.",
        "provider": {
          "@type": "Organization",
          "name": "Video Book Review Specialist",
          "url": "https://enrolment.bookreviewspecialists.app"
        },
        "educationalCredentialAwarded": "CPD Accredited Video Book Review Specialist Certificate",
        "courseMode": "Online",
        "timeRequired": "P6W",
        "occupationalCredentialAwarded": {
          "@type": "EducationalOccupationalCredential",
          "name": "CPD Accredited Video Book Review Specialist",
          "credentialCategory": "Professional Certificate",
          "recognizedBy": {
            "@type": "Organization",
            "name": "CPD Certification Service"
          }
        },
        "offers": {
          "@type": "Offer",
          "price": "9.99",
          "priceCurrency": "GBP",
          "name": "Course Enrolment",
          "availability": "https://schema.org/InStock",
          "url": "https://enrolment.bookreviewspecialists.app/#pricing"
        },
        "hasCourseInstance": {
          "@type": "CourseInstance",
          "courseMode": "Online",
          "courseWorkload": "PT10H"
        }
      },
      {
        "@type": "WebPage",
        "@id": "https://enrolment.bookreviewspecialists.app/become-a-video-book-review-specialist",
        "url": "https://enrolment.bookreviewspecialists.app/become-a-video-book-review-specialist",
        "name": "Become a Video Book Review Specialist | CPD Accredited Training",
        "description": "Train to become a CPD Accredited Video Book Review Specialist. A new digital service opportunity in the author economy.",
        "isPartOf": {
          "@id": "https://enrolment.bookreviewspecialists.app/#website"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <SEO 
        title="Become a Video Book Review Specialist | CPD Accredited Training"
        description="Train to become a CPD Accredited Video Book Review Specialist. Create AI-powered book review videos without reading, being on camera, or writing scripts. Start from £9.99. Work from home."
        canonical="/become-a-video-book-review-specialist"
        schema={becomeSpecialistSchema}
      />
      
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-3">
            <a href="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                alt="Video Book Review Specialist Logo"
                className="w-10 h-10 rounded-full object-cover"
              />
              <span className="text-xl font-bold text-slate-800">Video Book Review Specialist</span>
            </a>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-slate-600 hover:text-slate-800 text-sm font-medium">
                Home
              </Link>
              <Link to="/book-video-reviews-for-authors" className="text-pink-600 hover:text-pink-700 text-sm font-medium">
                For Authors
              </Link>
              <Link to="/student-login" className="text-green-600 hover:text-green-700 text-sm font-medium">
                Student Login
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Banner Image */}
      <div className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/6ub4o9m2_AISelect_20260306_162311_Chrome.jpg"
          alt="Get Paid to Review Books Without Reading Them or Being on Camera - Video Book Review Specialist £9.99"
          className="w-full h-auto"
        />
      </div>

      {/* CPD Training Banner */}
      <div className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/kzlz1y4t_Become%20a%20video%20book%20review%20specialist%20today.png"
          alt="CPD Accredited Training - Become a Video Book Review Specialist - Launch or Enhance Your Career in a New Niche Author Service"
          className="w-full h-auto"
        />
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
              <p className="text-sm font-semibold">A New Niche Digital Service in the Growing Author Economy</p>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Become a CPD Accredited<br />Video Book Review Specialist
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90">
              Turn Your Laptop Into a Home-Based Business Helping Authors Promote Their Books — Even If You've Never Reviewed a Book Before
            </p>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8 max-w-2xl mx-auto">
              <p className="text-lg italic mb-2">"A job in the book industry nobody talks about"</p>
              <p className="text-lg italic">"Authors will really pay for this"</p>
            </div>

            <Link to="/enroll">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6 rounded-xl">
                Secure Your Enrolment – £9.99
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Quiz Banner */}
          <a 
            href="https://qualiquiz.app/quiz/what-is-the-most-realistic-way-for-you-to-build-income-right-now"
            target="_blank"
            rel="noopener noreferrer"
            className="block mb-8 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl p-6 text-center hover:opacity-90 transition-opacity shadow-lg"
          >
            <p className="text-xl md:text-2xl font-semibold">
              Click to find out if this is for you by answering a few questions
            </p>
          </a>

          {/* YouTube Video */}
          <div className="mb-12 flex justify-center">
            <div className="aspect-[9/16] w-full max-w-sm rounded-xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.youtube.com/embed/N3WJWCLB2SQ"
                title="Video Book Review Specialist Introduction"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
            <p className="text-xl text-slate-700 leading-relaxed mb-6">
              Thousands of authors publish new books every week — but most struggle to get noticed online.
            </p>
            <p className="text-xl text-slate-700 leading-relaxed mb-6">
              Video book reviews have become one of the most powerful ways to promote books across YouTube, TikTok, Instagram, and Amazon.
            </p>
            <p className="text-xl text-slate-700 leading-relaxed mb-8">
              This CPD accredited training shows you how to use AI-assisted systems and simple video frameworks to create professional video book reviews and build a flexible home-based service helping authors market their books.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-1" />
                <p className="text-slate-700">Learn how to create engaging video book reviews</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-1" />
                <p className="text-slate-700">Use AI tools to analyse books quickly and structure reviews</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-1" />
                <p className="text-slate-700">Offer promotional services to authors and publishers</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-1" />
                <p className="text-slate-700">Build a flexible home-based digital service business</p>
              </div>
            </div>

            <p className="text-lg text-slate-600 mb-2">No publishing experience required.</p>
            <p className="text-lg text-slate-600 mb-2">No expensive equipment needed.</p>
            <p className="text-lg text-slate-600 mb-8">Just a laptop, internet connection, and the right system.</p>

            <div className="text-center">
              <a href="https://enrolment.bookreviewspecialists.app/#pricing" target="_blank" rel="noopener noreferrer">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-4">
                  Start Your Journey Today
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* New Opportunity Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-8 text-center">
              A New Opportunity in the Global Author Economy
            </h2>
            
            <div className="space-y-6 text-lg text-slate-700 mb-12">
              <p>Every year, millions of new books are published worldwide.</p>
              <p>Self-publishing platforms and independent publishing have opened the door for thousands of new authors entering the market every month.</p>
              <p>Yet the biggest challenge most authors face is <strong>visibility</strong>.</p>
              <p>Without exposure, even great books remain undiscovered.</p>
            </div>

            <Card className="bg-blue-50 border-blue-200 p-8 mb-12">
              <h3 className="text-2xl font-semibold text-slate-800 mb-6">Video reviews help authors:</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-slate-700">Introduce their books to new audiences</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-slate-700">Create promotional content for social media</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-slate-700">Improve credibility through independent commentary</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-slate-700">Increase engagement around their book launch</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-slate-700">Provide marketing assets for their websites and Amazon pages</p>
                </div>
              </div>
            </Card>

            <p className="text-xl text-slate-700 text-center mb-8">
              Authors actively seek people who can help them create this type of promotional content.<br />
              <strong>That is the role of a Video Book Review Specialist.</strong>
            </p>
          </div>
        </div>
      </div>

      {/* YouTube Video Gallery */}
      <div className="bg-slate-100 py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-8 text-center">
            See Our Video Book Reviews in Action
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/G8Cqd4Kxat8"
                title="Video Book Review 1"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/1wfX2MCtNJU"
                title="Video Book Review 2"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/Ris4ZveVhEQ"
                title="Video Book Review 3"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/CPOb9I9vhlA"
                title="Video Book Review 4"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/4GtmJD7-bOI"
                title="Video Book Review 5"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/oIG8HHNZzM4"
                title="Video Book Review 6"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/49tgEZ-RpyM"
                title="Video Book Review 7"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/k2Xn6R6R7PI"
                title="Video Book Review 8"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/Ota82P8TfLo"
                title="Video Book Review 9"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/kCB286GunMI"
                title="Video Book Review 10"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* What Does a Specialist Do */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-8 text-center">
            What Does a Video Book Review Specialist Do?
          </h2>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
            <p className="text-xl text-slate-700 mb-8">
              A Video Book Review Specialist creates short, engaging videos that introduce books to readers.
            </p>

            <h3 className="text-2xl font-semibold text-slate-800 mb-6">These videos may include:</h3>
            <ul className="space-y-3 mb-8 text-lg text-slate-700">
              <li className="flex items-start">
                <Video className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-1" />
                60-second promotional book reviews
              </li>
              <li className="flex items-start">
                <Video className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-1" />
                YouTube review content
              </li>
              <li className="flex items-start">
                <Video className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-1" />
                Instagram Reels and TikTok book introductions
              </li>
              <li className="flex items-start">
                <Video className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-1" />
                Book launch promotional videos
              </li>
              <li className="flex items-start">
                <Video className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-1" />
                Author interview highlights
              </li>
              <li className="flex items-start">
                <Video className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-1" />
                AI-assisted review commentary
              </li>
            </ul>

            <p className="text-lg text-slate-700 leading-relaxed">
              Instead of spending days reading every book from cover to cover, specialists learn modern methods to analyse and structure reviews efficiently using AI-assisted tools and professional frameworks. This allows reviews to be produced quickly while still offering insight and credibility.
            </p>
          </div>
        </div>
      </div>

      {/* CPD Accredited */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Award className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">CPD Accredited Professional Training</h2>
            <p className="text-xl mb-8 text-white/90">
              This programme provides Continuing Professional Development (CPD) accredited training, helping participants develop professional skills in video book review creation, AI-assisted content analysis, digital storytelling, and business setup.
            </p>
          </div>
        </div>
      </div>

      {/* Build Flexible Business */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-8 text-center">
            Build a Flexible Business From Home
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="p-6 text-center">
              <Home className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Work From Home</h3>
              <p className="text-slate-600">Operate entirely from your laptop</p>
            </Card>
            <Card className="p-6 text-center">
              <Globe className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Global Clients</h3>
              <p className="text-slate-600">Work with authors worldwide</p>
            </Card>
            <Card className="p-6 text-center">
              <TrendingUp className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Scalable Income</h3>
              <p className="text-slate-600">Part-time or full-time</p>
            </Card>
          </div>

          {/* Promotional Video */}
          <div className="my-12">
            <video 
              controls 
              className="w-full max-w-3xl mx-auto rounded-xl shadow-2xl"
              poster=""
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/otbq5c6z_Project_3010_6eb6d800-1b01-45e1-8ddc-96bf72ea7f0d.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h3 className="text-2xl font-semibold text-slate-800 mb-6">All you need is:</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                <span className="text-slate-700">A laptop or tablet</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                <span className="text-slate-700">Internet connection</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                <span className="text-slate-700">AI tools</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                <span className="text-slate-700">Basic video creation software</span>
              </div>
            </div>
            <p className="text-lg text-slate-700">
              Many specialists choose to produce multiple reviews each week, offering packages to authors and publishers.
            </p>
          </div>
        </div>
      </div>

      {/* Perfect For */}
      <div className="bg-slate-100 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-8 text-center">
              Perfect For People Who Want To…
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Start a flexible home-based business</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Work online with authors worldwide</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Enter the publishing and media industry</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Use AI tools productively rather than passively</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Create a niche digital service with real demand</p>
              </Card>
              <Card className="p-6">
                <CheckCircle className="w-8 h-8 text-green-600 mb-4" />
                <p className="text-lg text-slate-700">Upgrade an existing blog, publishing service, or YouTube channel</p>
              </Card>
            </div>

            <p className="text-xl text-center text-slate-700 mt-8">
              <strong>No previous publishing experience is required.</strong><br />
              The training is designed to guide beginners step-by-step.
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Start Your Journey Today
            </h2>
            <p className="text-xl mb-8 text-white/90">
              If you're looking for a practical way to build a flexible business from home while helping authors bring their books to life online, this training provides the skills and systems to get started.
            </p>
            <p className="text-xl mb-8 text-white/90">
              Secure your place today and take the first step towards becoming a CPD Accredited Video Book Review Specialist.
            </p>
            <a href="https://enrolment.bookreviewspecialists.app/#pricing" target="_blank" rel="noopener noreferrer">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-10 py-6 rounded-xl text-xl">
                Start Your Enrolment – £9.99
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BecomeSpecialist;
